'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { createClient } from '@/lib/supabase/client';
import Icon from '@/components/ui/AppIcon';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email || !/^\S+@\S+$/.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    setIsLoading(true);
    try {
      const supabase = createClient();
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${process.env.NEXT_PUBLIC_SITE_URL}/auth/reset-password`,
      });

      if (resetError) {
        setError(resetError.message);
      } else {
        setSubmitted(true);
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="w-full max-w-md mx-auto text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
            <Icon name="EnvelopeIcon" size={32} className="text-emerald-600" />
          </div>
          <h1 className="text-2xl font-700 text-foreground mb-3">Check your inbox</h1>
          <p className="text-muted-foreground text-sm mb-2">
            We sent a password reset link to:
          </p>
          <p className="text-foreground font-600 text-sm mb-6 bg-muted rounded-xl px-4 py-2 inline-block">
            {email}
          </p>
          <p className="text-muted-foreground text-sm mb-8">
            Click the link in the email to reset your password. If you don&apos;t see it, check your spam folder.
          </p>
          <div className="space-y-3">
            <button
              onClick={() => { setSubmitted(false); setEmail(''); }}
              className="w-full border border-border bg-card hover:bg-muted rounded-xl px-4 py-3 text-sm font-500 text-foreground transition-all duration-150"
            >
              Try a different email
            </button>
            <Link
              href="/sign-up-login-screen"
              className="block w-full text-center text-sm text-primary font-500 hover:opacity-80 transition-opacity duration-150 py-2"
            >
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="w-full max-w-md mx-auto">
        <div className="mb-8 text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
            <Icon name="LockClosedIcon" size={32} className="text-emerald-600" />
          </div>
          <h1 className="text-2xl font-700 text-foreground mb-2">Forgot your password?</h1>
          <p className="text-muted-foreground text-sm">
            Enter your email and we&apos;ll send you a secure reset link.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="reset-email" className="block text-sm font-500 text-foreground mb-1.5">
              Email address
            </label>
            <input
              id="reset-email"
              type="email"
              autoComplete="email"
              placeholder="ahmed@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`w-full bg-muted border rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-150 ${
                error ? 'border-red-400 bg-red-50' : 'border-border'
              }`}
            />
            {error && (
              <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
                <Icon name="ExclamationCircleIcon" size={14} className="text-red-600" />
                {error}
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full gradient-teal text-white rounded-xl px-4 py-3.5 text-sm font-600 hover:opacity-90 active:scale-95 transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            style={{ minHeight: '48px' }}
          >
            {isLoading ? (
              <>
                <Icon name="ArrowPathIcon" size={18} className="animate-spin" />
                Sending reset link...
              </>
            ) : (
              'Send Reset Link'
            )}
          </button>
        </form>

        <p className="text-center text-sm text-muted-foreground mt-6">
          Remember your password?{' '}
          <Link
            href="/sign-up-login-screen"
            className="text-primary font-600 hover:opacity-80 transition-opacity duration-150"
          >
            Back to sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
